<template lang="pug">
.v-auth.auth(
		ref="sheet"
		:class="[data?.className, {'hidden': hidden, 'auth--dragging': dragging}]"
		:style="dragStyle")
	.auth__window(v-if="data")
		.auth__grabber(@pointerdown="onGrabStart")
		.auth__modal-nav.modal-nav
			button.modal-nav__back(
					type="button"
					v-if="data.backButton"
					@click="changeNumber")
				v-svg-icon.modal-nav__back-icon(icon="arrow-left")
			h2.modal-nav__title(v-if="data.title && data?.className === 'choice'" ) {{ data.title }}
			button.modal-nav__close.js-close-modal-gallery(type="button")
				v-svg-icon.modal-nav__close-icon(icon="close")

		//- data-scroll-lock-scrollable: при открытой модалке scroll-lock глушит
		//- touchmove везде, кроме зарегистрированных скроллируемых элементов
		//- (разрешён только .modal__surface). Без атрибута тело формы в мобильной
		//- шторке не скроллилось пальцем при переполнении (в т.ч. с клавиатурой).
		.auth__body(data-scroll-lock-scrollable="")
			template(v-if="!isUserGroupStep")
				.auth__title
					img.svg-icon.auth__title-icon(
							v-if="data.image"
							:src="data.image?.src"
							:alt="data.image?.alt")
					span.auth__title-text {{ computedTitle }}
			template(v-else)
				template(v-for="({id, mainTitle}, index) in data.tabs" :key="index")
					.auth__title(v-if="id === activeTabId")
						img.svg-icon.auth__title-icon(
								v-if="data.image"
								:src="data.image?.src"
								:alt="data.image?.alt")
						span.auth__title-text(v-if="mainTitle") {{ mainTitle }}

			v-form(:data-source="data"
					:forced-tab-id="forcedTabId"
					:hidden-tabs-panel="isLoginRegisterForm && authMode === 'register'"
					:show-confirm="!isLoginRegisterForm || authMode === 'register'"
					:force-confirmed="isLoginRegisterForm && authMode === 'login'"
					:submit-button-text="computedButtonText"
					:hide-group-note="isLoginRegisterForm && authMode === 'login' && activeTabId === emailTabId"
					:phone-step-note="isLoginRegisterForm && activeTabId === phoneTabId ? phoneStepNote : null"
					:code-step="isPhoneCodeStep ? {phone: formattedPhone, enterCodeText: t('enterSmsCode'), codeSentToText: t('codeSentTo'), changeNumberText: t('changeNumber')} : null"
					@form-submited="handleFormSubmitted"
					@update-out="dataSet"
					@updateTabId="updateTabId"
					@change-number="changeNumber"
					@redirect="hidden = true")

			.auth__forgot-password(
					v-if="isLoginRegisterForm && authMode === 'login' && activeTabId === emailTabId && forgotPasswordNote"
					v-html="forgotPasswordNote")

			.auth__switch(v-if="isLoginRegisterForm")
				span.auth__switch-label {{ authMode === 'register' ? t('haveAccount') : t('noAccount') }}
				button.auth__switch-btn.btn.btn--round(type="button" @click="toggleAuthMode")
					| {{ authMode === 'register' ? t('login') : t('register') }}
</template>

<script>

import sourceMixin from "../components/mixins/v-mixin-source.vue";
import controller from "../scripts/controller/controller.js";
import { markRaw } from "vue";

export default {
	mixins: [
		sourceMixin
	],

	data() {
		return {
			data: null,
			activeTabId: null,
			hidden: false,
			authMode: 'login',
			forcedTabId: null,
			// id табов "По e-mail"/"По телефону" фиксируются один раз при
			// первой полной загрузке формы: на промежуточных AJAX-шагах
			// (ввод кода, выбор роли) сервер схлопывает data.tabs до одного
			// служебного таба "empty", поэтому вычислять id на лету нельзя
			emailTabId: null,
			phoneTabId: null,
			// мобильная шторка (bottom-sheet)
			isSheet: false,     // форма открыта как мобильная шторка
			sheetShown: false,  // шторка уже выехала наверх
			dragging: false,
			dragStartY: 0,
			dragOffset: 0,
			// WebOTP работает только в Chromium; Safari использует тот же формат
			// SMS через autocomplete="one-time-code" в v-field-code.
			webOtpAbortController: null,
			webOtpTimeoutId: null,
			webOtpListening: false,
			pendingWebOtpCode: ''
		};
	},

	computed: {
		// inline transform шторки — применяем ТОЛЬКО в мобильной шторке,
		// чтобы не задеть десктоп/страницу. Весь transform (выезд/драг/
		// закрытие) управляется здесь одной непрерывной величиной, без
		// конфликта CSS-анимации и transition (см. задачи 1/2).
		dragStyle() {
			if (!this.isSheet) return null;
			if (!this.sheetShown) {
				return { transform: 'translateY(100%)' };
			}
			return { transform: `translateY(${this.dragOffset}px)` };
		},

		// разделение "Вход"/"Регистрация" — только для formы newsite:auth
		// (там есть оба именованных таба "По e-mail"/"По телефону").
		// Другие формы, использующие тот же v-auth/v-form (восстановление
		// пароля, смена email/телефона в ЛК — auth.email.reset.password,
		// auth.change.email, auth/templates/edit), не имеют этих табов и
		// должны рендериться как раньше, без переключателя режимов
		isLoginRegisterForm() {
			return !!(this.emailTabId && this.phoneTabId);
		},

		// шаг выбора роли после подтверждения кода — там нет переключения
		// вход/регистрация, форма сама решает, что показать дальше
		isUserGroupStep() {
			return this.data?.className === 'choice';
		},

		phoneAction() {
			const field = this.data?.fields?.find(f => f.code === 'PHONE_ACTION' || f.name === 'PHONE_ACTION');
			return field?.value || 'send-verify-code';
		},

		computedTitle() {
			if (!this.isLoginRegisterForm) {
				return this.data?.title || '';
			}
			return this.authMode === 'register' ? this.t('register') : this.t('login');
		},

		computedButtonText() {
			if (!this.isLoginRegisterForm) {
				return null;
			}
			if (this.activeTabId === this.phoneTabId && this.phoneAction === 'send-verify-code') {
				return this.t('getSmsCode');
			}
			return this.t('login');
		},

		// ссылка "Забыли пароль?" приходит в группе e-mail-таба (group.note,
		// см. GROUP_LIST_SUB_NAME_* в login.php) — выводим её отдельно под
		// кнопкой "Войти", а не внутри группы полей (см. hideGroupNote)
		forgotPasswordNote() {
			const group = this.data?.groups?.find(g => g.tabId === this.emailTabId && g.note);
			return group?.note || null;
		},

		// текст-подсказка над полем телефона (INNER_LABEL поля PERSONAL_MOBILE
		// в login.php) — v-field-text.vue не рендерит field.note для текстовых
		// полей, поэтому выводим его сами (внутри v-form.vue, под панелью вкладок)
		phoneStepNote() {
			if (this.phoneAction !== 'send-verify-code') return null;
			const field = this.data?.fields?.find(f => f.code === 'PERSONAL_MOBILE' || f.name === 'PERSONAL_MOBILE');
			return field?.note || null;
		},

		hasAuthPhoneField() {
			return !!this.data?.fields?.some(field => field.type === 'auth-phone');
		},

		// поле кода из СМС (тип "code") на шаге подтверждения телефона —
		// нужно, чтобы показать над клетками "Введите код из СМС / Отправили
		// его на <номер>" + ссылку "Изменить номер"
		codeField() {
			return this.data?.fields?.find(f => f.type === 'code') || null;
		},

		// шаг ввода кода из СМС при входе/регистрации по телефону
		// (для reset-password код приходит на email, не телефон — там phone пуст)
		isPhoneCodeStep() {
			return !!(this.codeField && this.codeField.phone);
		},

		// Номер для подписи "Отправили его на ..." форматирует сервер по
		// выбранной стране. Фолбэк нужен только для старого/кэшированного ответа.
		formattedPhone() {
			if (this.codeField?.phoneFormatted) {
				return this.codeField.phoneFormatted;
			}
			const digits = String(this.codeField?.phone || '').replace(/\D/g, '');
			return digits ? `+${digits}` : '';
		}
	},

	methods: {
		isWebOtpSupported() {
			return typeof window !== 'undefined'
				&& 'OTPCredential' in window
				&& typeof navigator?.credentials?.get === 'function'
				&& typeof AbortController !== 'undefined';
		},

		shouldStartWebOtp(options = {}) {
			if (options.action === 'new-code') {
				return this.isPhoneCodeStep;
			}
			if (options.action && !['submit', 'autosubmit'].includes(options.action)) {
				return false;
			}
			if (this.phoneAction !== 'send-verify-code') {
				return false;
			}
			if (this.isLoginRegisterForm) {
				return this.activeTabId === this.phoneTabId;
			}
			return this.hasAuthPhoneField;
		},

		handleFormSubmitted(options = {}) {
			if (this.shouldStartWebOtp(options)) {
				this.startWebOtp();
			} else if (this.isPhoneCodeStep) {
				// Пользователь отправил код вручную — поздний ответ WebOTP уже не
				// должен менять форму во время перехода на следующий шаг.
				this.stopWebOtp();
			}
		},

		startWebOtp() {
			if (!this.isWebOtpSupported()) return;

			this.stopWebOtp(true);
			const controller = markRaw(new AbortController());
			this.webOtpAbortController = controller;
			this.webOtpListening = true;
			this.webOtpTimeoutId = window.setTimeout(() => {
				if (this.webOtpAbortController === controller) {
					this.stopWebOtp();
				}
			}, 5 * 60 * 1000);

			navigator.credentials.get({
				otp: {transport: ['sms']},
				signal: controller.signal
			}).then((credential) => {
				if (this.webOtpAbortController !== controller || !credential?.code) return;
				this.pendingWebOtpCode = String(credential.code);
				this.applyPendingWebOtpCode();
			}).catch((error) => {
				if (!['AbortError', 'NotAllowedError'].includes(error?.name)) {
					console.warn('WebOTP request failed', error);
				}
			}).finally(() => {
				if (this.webOtpAbortController === controller) {
					this.clearWebOtpState();
				}
			});
		},

		applyPendingWebOtpCode() {
			if (!this.pendingWebOtpCode || !this.codeField) return false;

			const length = Number(this.codeField.lengthCode) || 4;
			const code = this.pendingWebOtpCode.replace(/\D/g, '').slice(0, length);
			if (code.length !== length) return false;

			this.codeField.value = code;
			this.pendingWebOtpCode = '';
			return true;
		},

		clearWebOtpState() {
			if (this.webOtpTimeoutId) {
				window.clearTimeout(this.webOtpTimeoutId);
			}
			this.webOtpTimeoutId = null;
			this.webOtpAbortController = null;
			this.webOtpListening = false;
		},

		stopWebOtp(clearPendingCode = false) {
			if (this.webOtpAbortController) {
				this.webOtpAbortController.abort();
			}
			this.clearWebOtpState();
			if (clearPendingCode) {
				this.pendingWebOtpCode = '';
			}
		},

		hasFormErrors(data) {
			return !!(data?.errors?.form?.length || data?.errors?.fields?.length);
		},

		// локализованная строка из data.i18n (приходит с сервера, см.
		// template.php). Фолбэк на русский — на случай форм, которые ещё
		// не пробрасывают i18n (v-auth переиспользуется несколькими
		// компонентами)
		t(key) {
			const fallback = {
				login: 'Войти',
				register: 'Регистрация',
				getSmsCode: 'Получить код из СМС',
				noAccount: 'Нет аккаунта?',
				haveAccount: 'Уже есть аккаунт?',
				enterSmsCode: 'Введите код из СМС',
				codeSentTo: 'Отправили его на',
				changeNumber: 'Изменить номер'
			};
			return this.data?.i18n?.[key] || fallback[key] || '';
		},

		// клик по "хваталке" мобильной шторки — закрываем модалку целиком
		closeSheet() {
			if (window.modalController) {
				window.modalController.closeAll();
			}
		},

		// ==== перетаскивание шторки за "хваталку" (только мобильная шторка) ====
		onGrabStart(e) {
			if (!this.isSheet) return;
			this.dragging = true;
			this.dragStartY = e.clientY;
			this.dragOffset = 0;
			window.addEventListener('pointermove', this.onGrabMove);
			window.addEventListener('pointerup', this.onGrabEnd);
			window.addEventListener('pointercancel', this.onGrabEnd);
		},

		onGrabMove(e) {
			if (!this.dragging) return;
			// тянем только вниз (offset >= 0)
			this.dragOffset = Math.max(0, e.clientY - this.dragStartY);
		},

		onGrabEnd() {
			if (!this.dragging) return;
			window.removeEventListener('pointermove', this.onGrabMove);
			window.removeEventListener('pointerup', this.onGrabEnd);
			window.removeEventListener('pointercancel', this.onGrabEnd);

			const sheetHeight = this.$refs.sheet?.offsetHeight || window.innerHeight;
			const dragged = this.dragOffset;
			// снимаем dragging — включается transition, шторка едет плавно
			this.dragging = false;

			if (dragged > Math.min(sheetHeight * 0.3, 160)) {
				// закрываем: доводим шторку ВНИЗ за край экрана (продолжаем
				// движение пальца вниз, НЕ вверх), и только после того как она
				// уехала — закрываем модалку
				this.dragOffset = window.innerHeight;
				setTimeout(() => {
					this.closeSheet();
				}, 300);
			} else {
				// не дотянули — плавно возвращаем на место
				this.dragOffset = 0;
			}
		},

		dataSet(data) {
			this.data = data;
			this.$nextTick(() => {
				if (this.isPhoneCodeStep) {
					this.applyPendingWebOtpCode();
				} else if (this.webOtpListening && this.hasFormErrors(data)) {
					this.stopWebOtp();
				}
			});

			// activeTabId НЕ берём из data вслепую: сервер может прислать
			// устаревшую вкладку (напр. e-mail после "изменить номер"), которая
			// перебивает реальный выбор. Актуальную вкладку даёт v-form через
			// событие updateTabId (единый источник правды). Берём серверный
			// activeTabId только как fallback, если своего значения ещё нет
			if (!this.activeTabId && data.activeTabId) {
				this.activeTabId = data.activeTabId;
			}

			// табы "По e-mail"/"По телефону" ищутся по названию (не по
			// индексу — data.tabs[0] это служебный пустой таб "empty",
			// реальные табы начинаются дальше) и фиксируются один раз,
			// пока data.tabs действительно содержит оба именованных таба.
			// Формы без обоих табов (reset-password и т.п.) их не найдут —
			// isLoginRegisterForm останется false, что и требуется
			const namedTabs = data?.tabs?.filter(t => t.title) || [];
			if (!this.phoneTabId && !this.emailTabId && namedTabs.length >= 2) {
				this.emailTabId = (namedTabs.find(t => /e-mail/i.test(t.title)) || namedTabs[0])?.id || null;
				this.phoneTabId = (namedTabs.find(t => /телефон|phone/i.test(t.title)) || namedTabs[1])?.id || null;

				// при первой загрузке экрана входа форсируем таб "По e-mail"
				// (он уже первый по умолчанию, форс на случай, если сервер
				// когда-либо пришлёт другой activeTabId) — таково поведение
				// экрана "Войти" по умолчанию
				if (this.authMode === 'login') {
					this.forcedTabId = this.emailTabId;
				}
			}
		},

		updateTabId(value) {
			this.activeTabId = value;
			// синхронизируем forcedTabId с реальным выбором пользователя —
			// иначе после AJAX-ответа с пустым activeTabId (см. prepareDataTemplates
			// в vueForms/class.php — сервер всегда шлёт activeTabId: "")
			// v-form.vue откатит currentTabId на старый forcedTabId
			if (value && (value === this.emailTabId || value === this.phoneTabId)) {
				this.forcedTabId = value;
			}
		},

		toggleAuthMode() {
			this.stopWebOtp(true);
			this.authMode = this.authMode === 'register' ? 'login' : 'register';
			this.forcedTabId = this.authMode === 'register' ? this.phoneTabId : this.emailTabId;

			// внутри шагов SMS-кода/выбора роли сервер схлопывает data.tabs
			// до одного служебного таба — переключить вкладку на лету уже
			// нельзя, поэтому сбрасываем форму на начальный экран
			if (!this.data?.tabs || this.data.tabs.length < 2) {
				this.requestDataLoad();
			}
		},

		// "Изменить номер" на шаге кода: возвращаемся к вводу телефона,
		// сохраняя текущий режим (вход/регистрация) и телефонную вкладку —
		// НЕ на дефолтный экран входа по e-mail
		changeNumber() {
			this.stopWebOtp(true);
			if (this.isLoginRegisterForm && this.phoneTabId) {
				this.forcedTabId = this.phoneTabId;
			}
			this.requestDataLoad();
		},

		requestDataLoad() {
			this.dataLoad({
				url: this.data.remote,
				data: {[`action`]: 'clear'},
				delay: true,
			})
		},


	},

	mounted() {
		// если форма отрендерена внутри модального окна (а не на странице),
		// вешаем на корневую .modal класс modal--auth — по нему CSS включает
		// мобильную шторку (bottom-sheet). На странице .modal-предка нет
		const modalEl = this.$el.closest('.modal');
		if (modalEl) {
			this._modalEl = modalEl;
			modalEl.classList.add('modal--auth');

			// на мобильных запускаем выезд шторки снизу вверх: рендерим за
			// нижним краем (sheetShown=false → translateY 100%), затем на
			// следующем кадре поднимаем (sheetShown=true → translateY 0),
			// transition проигрывает плавный выезд
			if (window.matchMedia('(max-width: 767px)').matches) {
				this.isSheet = true;
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						this.sheetShown = true;
					});
				});
			}
		}
	},

	beforeUnmount() {
		this.stopWebOtp(true);
		// снимаем drag-слушатели, если компонент размонтировали во время
		// перетаскивания (иначе они повиснут на window)
		window.removeEventListener('pointermove', this.onGrabMove);
		window.removeEventListener('pointerup', this.onGrabEnd);
		window.removeEventListener('pointercancel', this.onGrabEnd);
		// снимаем класс шторки с .modal, чтобы он не остался для следующей,
		// не-auth модалки (иначе её фон затемнится и confirmCloseAuth
		// сработает не на той модалке)
		if (this._modalEl) {
			this._modalEl.classList.remove('modal--auth');
		}
	}
};
</script>
